import { CommandCenterHeader } from "@components";
import { Role } from "@enums";
import { Card, Stack } from "@mui/material";
import CommandCenterTable from "@pages/CommandCenter/CommandCenterTable";
import SearchAndFilterBar, {
  SearchFilterOption,
} from "@pages/CommandCenter/SearchAndFilterBar";
import {
  useLazyWebApiV1SearchOrdersQuery,
  useWebApiV1GetUserPreferencesQuery,
} from "@providers/hop-ord-server/api";
import { useCheckUserRole } from "@utils";
import { debounce } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";

// RO default statuses when no user preference statuses are provided
const roDefaultStatuses: string[] = [
  SearchFilterOption.PENDING_PLAN_REVIEW,
  SearchFilterOption.APPROVED,
  SearchFilterOption.REJECTED,
];

const searchTextToolTips = [
  "Check if you have any filters applied.",
  "Verify your spelling and try again.",
  "Just received a new careplan? Please wait one minute and refresh the page. ",
  "Try using the patient's first name and last name or patient ID.",
];

const noSearchTextToolTips = [
  "Adjusting or clearing some filters.",
  "Checking if there are any active search terms.",
];

const CommandCenter = () => {
  const { data: userPreferenceData } = useWebApiV1GetUserPreferencesQuery();

  const tenantIds = userPreferenceData?.tenant_ids;
  const isRo = useCheckUserRole(Role.RO);

  // All the good stuff for the order search
  const [searchText, setSearchText] = useState<string>("");
  const [debouncedSearchText, setDebouncedSearchText] = useState<string>("");

  const [selectedStatus, setSelectedStatus] = useState<string[]>(
    userPreferenceData?.statuses || [],
  );
  const [selectedSite, setSelectedSite] = useState<string[]>(
    userPreferenceData?.treating_departments || [],
  );
  const [selectedPhysician, setSelectedPhysician] = useState<string[]>(
    userPreferenceData?.physicians || [],
  );
  const [selectedQuickFilters, setSelectedQuickFilters] = useState<string[]>(
    userPreferenceData?.quick_filters || [],
  );
  const [selectedAssignedTo, setSelectedAssignedTo] = useState<string[]>(
    userPreferenceData?.assigned_to_filter || [],
  );

  const [trigger, { data, isLoading }] = useLazyWebApiV1SearchOrdersQuery({
    pollingInterval: 30_000,
  });

  useEffect(() => {
    let statusFilter = selectedStatus.join(",");

    if (isRo && selectedStatus.length === 0) {
      statusFilter = roDefaultStatuses.join(",");
    }

    trigger({
      searchText: debouncedSearchText,
      siteFilter: selectedSite.join(","),
      physicianFilter: selectedPhysician.join(","),
      statusFilter: statusFilter,
      quickFilter: selectedQuickFilters.join(","),
      assignedToFilter: selectedAssignedTo.join(","),
      ordering: "-submitted_datetime",
    });
  }, [
    debouncedSearchText,
    selectedSite,
    selectedPhysician,
    selectedStatus,
    selectedQuickFilters,
    selectedAssignedTo,
    isRo,
    trigger,
  ]);

  useEffect(() => {
    if (userPreferenceData) {
      // Only override statuses if the preference contains a value; otherwise apply RO defaults when applicable
      if (userPreferenceData?.statuses?.length) {
        setSelectedStatus(userPreferenceData.statuses || []);
      } else if (isRo) {
        setSelectedStatus(roDefaultStatuses);
      } else {
        setSelectedStatus([]);
      }
      setSelectedSite(userPreferenceData?.treating_departments || []);
      setSelectedPhysician(userPreferenceData?.physicians || []);
      setSelectedQuickFilters(userPreferenceData?.quick_filters || []);
      setSelectedAssignedTo(userPreferenceData?.assigned_to_filter || []);
    }
  }, [userPreferenceData, isRo]);

  // Addded debounce to the search text to prevent lots of spam
  const debouncedUpdateSearchText = useMemo(
    () =>
      debounce((v) => {
        setDebouncedSearchText(v);
      }, 1000),
    [setDebouncedSearchText],
  );

  const handleSearchTextChange = useCallback(
    (text: string) => {
      setSearchText(text);
      debouncedUpdateSearchText(text);
    },
    [debouncedUpdateSearchText],
  );

  useEffect(() => {
    return () => {
      debouncedUpdateSearchText.cancel();
    };
  }, [debouncedUpdateSearchText]);

  const noResultsMessage = searchText ? ` for "${searchText}"` : "";
  const toolTips = searchText ? searchTextToolTips : noSearchTextToolTips;

  return (
    <Stack
      sx={{
        gap: 3,
        padding: 2,
        height: 1,
        maxHeight: 1,
        width: 1,
        maxWidth: "1700px",
        minWidth: isLoading || !data?.length ? "1700px" : "auto",
        marginLeft: "auto",
        marginRight: "auto",
      }}
    >
      <CommandCenterHeader title="Command Center" />
      <SearchAndFilterBar
        setSearchText={handleSearchTextChange}
        searchText={searchText}
        selectedStatus={selectedStatus}
        setSelectedStatus={setSelectedStatus}
        selectedSite={selectedSite}
        setSelectedSite={setSelectedSite}
        selectedPhysician={selectedPhysician}
        setSelectedPhysician={setSelectedPhysician}
        selectedQuickFilters={selectedQuickFilters}
        setSelectedQuickFilters={setSelectedQuickFilters}
        selectedAssignedTo={selectedAssignedTo}
        setSelectedAssignedTo={setSelectedAssignedTo}
        tenantIds={tenantIds ?? []}
      />
      <Card variant="outlined">
        <CommandCenterTable
          data={data}
          isLoading={isLoading}
          noResultsMessage={noResultsMessage}
          toolTips={toolTips}
          isRo={isRo}
        />
      </Card>
    </Stack>
  );
};

export default CommandCenter;
